import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { useApi, ApiError } from "@/lib/api";
import {
  useAdminUserDetail,
  useAdminUserWork,
  useAdminUserOnboarding,
  resetProgress,
  markOnboarded,
  resetOnboarding,
  saveCoachNote,
  clearCoachNote,
  type AdminUserDetail as DetailShape,
  type AdminWorkItem,
  type AdminUserOnboardingCourse
} from "@/lib/admin";
import { AppTopbar } from "@/components/AppTopbar";
import { CoursesCatalogDrawer } from "@/components/CoursesCatalogDrawer";
import { ConfirmDangerDialog } from "@/components/admin/ConfirmDangerDialog";

// Maps known error codes to friendly inline messages.
function friendlyError(e: unknown): string {
  if (!(e instanceof ApiError)) return e instanceof Error ? e.message : "An error occurred";
  switch (e.code) {
    case "LAST_ADMIN_LOCKOUT":
      return "Promote another admin first before changing this one's role.";
    case "SELF_DELETE_FORBIDDEN":
    case "SELF_DEMOTE_FORBIDDEN":
    case "SELF_LOCK_FORBIDDEN":
      return "Have another admin do this.";
    default:
      return e.message;
  }
}

export default function AdminUserDetail() {
  const { apiFetch } = useApi();
  const { userId = "" } = useParams<{ userId: string }>();
  const detail = useAdminUserDetail(userId);
  const work = useAdminUserWork(userId);
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <>
      <AppTopbar onOpenDrawer={() => setDrawerOpen(true)} />
      <CoursesCatalogDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <main className="p-8 max-w-5xl mx-auto space-y-6">
        {detail.status === "loading" && <p className="text-muted-foreground">Loading…</p>}
        {detail.status === "error" && (
          <p className="text-destructive">Failed to load detail: {detail.message}</p>
        )}
        {detail.status === "ready" && (
          <DetailContent
            detail={detail.detail}
            work={work}
            userId={userId}
            apiFetch={apiFetch}
            onResetSuccess={() => detail.refetch()}
            onDeleteSuccess={() => navigate("/admin")}
            onMutate={() => detail.refetch()}
          />
        )}
      </main>
    </>
  );
}

function DetailContent({
  detail, work, userId, apiFetch, onResetSuccess, onDeleteSuccess, onMutate
}: {
  detail: DetailShape;
  work: ReturnType<typeof useAdminUserWork>;
  userId: string;
  apiFetch: ReturnType<typeof useApi>["apiFetch"];
  onResetSuccess: () => void;
  onDeleteSuccess: () => void;
  onMutate: () => void;
}): JSX.Element {
  return (
    <>
      <ProfileCard detail={detail} />
      <OnboardingAnswersCard detail={detail} />
      <CourseOnboardingCard userId={userId} />
      <StatusCard detail={detail} userId={userId} apiFetch={apiFetch} onMutate={onMutate} />
      <CoachNoteCard detail={detail} userId={userId} apiFetch={apiFetch} onMutate={onMutate} />
      {detail.courses.length === 0 && (
        <Section title="Progress">
          <p className="text-muted-foreground text-sm">No courses on record for this user.</p>
        </Section>
      )}
      {detail.courses.map((course) => (
        <ProgressCard
          key={course.courseSlug}
          course={course}
          userId={userId}
          email={detail.user.email}
          apiFetch={apiFetch}
          onResetSuccess={onResetSuccess}
        />
      ))}
      <ActivityCard detail={detail} />
      <SavedWorkCard work={work} />
      <DangerZoneCard detail={detail} userId={userId} apiFetch={apiFetch} onDeleteSuccess={onDeleteSuccess} />
    </>
  );
}

function Section({ title, children, defaultOpen = true, tone = "default" }: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  tone?: "default" | "danger";
}): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  const wrapperCls = tone === "danger"
    ? "rounded-card border border-destructive/40 bg-destructive/5 p-6"
    : "rounded-card border border-border bg-card/40 p-6";
  const titleCls = tone === "danger"
    ? "text-sm uppercase tracking-wider text-destructive"
    : "text-sm uppercase tracking-wider text-primary-soft";
  return (
    <section className={wrapperCls}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between text-left"
        aria-expanded={open}
      >
        <h2 className={titleCls}>{title}</h2>
        <svg
          className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
      {open && <div className="mt-3">{children}</div>}
    </section>
  );
}

function ProfileCard({ detail }: { detail: DetailShape }): JSX.Element {
  const { user, presence } = detail;
  const online = presence !== null;
  const lastLogin = user.lastSignInAt ? new Date(user.lastSignInAt).toLocaleString() : "Never";
  const joined = new Date(user.createdAt).toLocaleDateString();
  return (
    <Section title="Profile">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xl font-semibold text-foreground">
              {user.name}
              {user.locked && (
                <span className="ml-2 inline-block rounded bg-warning/20 text-warning text-xs px-2 py-0.5 align-middle">
                  Locked
                </span>
              )}
            </div>
            <div className="font-mono text-xs text-muted-foreground">{user.email}</div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`inline-block h-2.5 w-2.5 rounded-full ${online ? "bg-success" : "bg-muted-foreground"}`} aria-hidden="true" />
            <span className="text-sm text-muted-foreground">{online ? "Online" : "Offline"}</span>
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-y-2 text-sm">
          <dt className="text-muted-foreground">First name</dt>
          <dd>{user.firstName ?? <span className="text-muted-foreground/60">—</span>}</dd>
          <dt className="text-muted-foreground">Last name</dt>
          <dd>{user.lastName ?? <span className="text-muted-foreground/60">—</span>}</dd>
          <dt className="text-muted-foreground">Role</dt>
          <dd>{user.role}</dd>
          <dt className="text-muted-foreground">Joined</dt>
          <dd>{joined}</dd>
          <dt className="text-muted-foreground">Last login</dt>
          <dd>{lastLogin}</dd>
        </dl>
      </div>
    </Section>
  );
}

const PORTAL_FIELD_LABELS: Record<string, string> = {
  first_name: "First name",
  last_name: "Last name",
  prior_experience: "Programming experience",
  college: "College",
  goals: "Goals",
  commitment: "Commitment",
};

function OnboardingAnswersCard({ detail }: { detail: DetailShape }): JSX.Element {
  const ob = detail.portalOnboarding;
  return (
    <Section title="Onboarding answers">
      {ob === null ? (
        <p className="text-sm text-muted-foreground">No onboarding responses yet.</p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground mb-4">
            Submitted {new Date(ob.submittedAt).toLocaleString()}
          </p>
          <dl className="space-y-4 text-sm">
            {ob.fieldIds.map((id) => {
              const val = ob.responses[id];
              if (val === undefined) return null;
              return (
                <div key={id}>
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground mb-0.5">
                    {PORTAL_FIELD_LABELS[id] ?? id}
                  </dt>
                  <dd className="text-foreground whitespace-pre-wrap">
                    {Array.isArray(val) ? val.join(", ") : val}
                  </dd>
                </div>
              );
            })}
          </dl>
        </>
      )}
    </Section>
  );
}

const ONBOARDING_STATUS_LABEL: Record<AdminUserOnboardingCourse["status"], string> = {
  "complete": "Submitted",
  "required-not-submitted": "Not submitted",
  "not-required": "No onboarding required"
};

function CourseOnboardingRow({ course }: { course: AdminUserOnboardingCourse }): JSX.Element {
  const statusLabel = course.status === "complete" && course.submittedAt !== null
    ? `Submitted ${new Date(course.submittedAt).toLocaleDateString()}`
    : ONBOARDING_STATUS_LABEL[course.status];
  return (
    <details className="rounded border border-border-subtle p-3">
      <summary className="cursor-pointer flex items-center justify-between gap-3 text-sm">
        <span className="font-semibold text-foreground">{course.courseTitle}</span>
        <span className="text-xs text-muted-foreground">{statusLabel}</span>
      </summary>
      {course.responses.length > 0 ? (
        <dl className="mt-3 space-y-3 text-sm">
          {course.responses.map((r) => (
            <div key={r.fieldId}>
              <dt className="text-xs uppercase tracking-wider text-muted-foreground mb-0.5">{r.label}</dt>
              <dd className="text-foreground whitespace-pre-wrap">
                {Array.isArray(r.value) ? r.value.join(", ") : r.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">No responses recorded.</p>
      )}
    </details>
  );
}

function CourseOnboardingCard({ userId }: { userId: string }): JSX.Element {
  const ob = useAdminUserOnboarding(userId);
  return (
    <Section title="Course onboarding">
      {ob.status === "loading" && <p className="text-sm text-muted-foreground">Loading…</p>}
      {ob.status === "error" && (
        <p className="text-sm text-destructive">Failed to load: {ob.message}</p>
      )}
      {ob.status === "ready" && (
        (ob.data.courses ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No per-course onboarding for this user.</p>
        ) : (
          <div className="space-y-2">
            {ob.data.courses.map((c) => <CourseOnboardingRow key={c.courseSlug} course={c} />)}
          </div>
        )
      )}
    </Section>
  );
}

function StatusRow({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 py-3 border-b border-border-subtle last:border-0">
      <div className="text-sm font-semibold text-foreground min-w-[120px]">{label}</div>
      <div className="flex-1 flex flex-wrap items-center justify-end gap-2">{children}</div>
    </div>
  );
}

function Badge({ tone, children }: { tone: "ok" | "muted"; children: React.ReactNode }): JSX.Element {
  const cls = tone === "ok" ? "bg-success/15 text-success" : "bg-muted text-muted-foreground";
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs ${cls}`}>
      {children}
    </span>
  );
}

function StatusCard({
  detail, userId, apiFetch, onMutate
}: {
  detail: DetailShape;
  userId: string;
  apiFetch: ReturnType<typeof useApi>["apiFetch"];
  onMutate: () => void;
}): JSX.Element {
  const { user } = detail;

  // Role change state
  const [showRoleChange, setShowRoleChange] = useState(false);
  const [newRole, setNewRole] = useState<"member" | "admin">(user.role === "admin" ? "member" : "admin");
  const [roleBusy, setRoleBusy] = useState(false);
  const [roleError, setRoleError] = useState<string | null>(null);

  // Onboarding state
  const [onboardingBusy, setOnboardingBusy] = useState(false);
  const [onboardingError, setOnboardingError] = useState<string | null>(null);

  // Lock/unlock state
  const [lockBusy, setLockBusy] = useState(false);
  const [lockError, setLockError] = useState<string | null>(null);

  const onboarded = user.onboardingCompletedAt !== null;
  const emailVerified = user.emailVerifiedAt !== null;

  async function changeRole() {
    setRoleBusy(true);
    setRoleError(null);
    try {
      await apiFetch(`/api/admin/users/${userId}/role`, {
        method: "PATCH",
        body: JSON.stringify({ role: newRole })
      });
      setShowRoleChange(false);
      onMutate();
    } catch (e) {
      setRoleError(friendlyError(e));
    } finally {
      setRoleBusy(false);
    }
  }

  async function toggleOnboarding() {
    setOnboardingBusy(true);
    setOnboardingError(null);
    try {
      if (onboarded) {
        await resetOnboarding(apiFetch, userId);
      } else {
        await markOnboarded(apiFetch, userId);
      }
      onMutate();
    } catch (e) {
      setOnboardingError(friendlyError(e));
    } finally {
      setOnboardingBusy(false);
    }
  }

  async function toggleLock() {
    setLockBusy(true);
    setLockError(null);
    try {
      await apiFetch(`/api/admin/users/${userId}/${user.locked ? "unlock" : "lock"}`, { method: "POST" });
      onMutate();
    } catch (e) {
      setLockError(friendlyError(e));
    } finally {
      setLockBusy(false);
    }
  }

  return (
    <Section title="Status">
      <div className="flex flex-col">
        {/* Role */}
        <StatusRow label="Role">
          <Badge tone="muted">{user.role}</Badge>
          {!showRoleChange ? (
            <button
              type="button"
              onClick={() => { setShowRoleChange(true); setRoleError(null); }}
              className="rounded bg-muted hover:bg-border px-3 py-1.5 text-sm font-semibold text-foreground"
            >
              Change role
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as "member" | "admin")}
                className="rounded bg-muted px-2 py-1 text-sm"
                disabled={roleBusy}
              >
                <option value="member">member</option>
                <option value="admin">admin</option>
              </select>
              <button
                type="button"
                onClick={changeRole}
                disabled={roleBusy}
                className="rounded bg-primary text-primary-foreground px-3 py-1 text-sm disabled:opacity-50"
              >
                {roleBusy ? "Saving…" : "Confirm"}
              </button>
              <button
                type="button"
                onClick={() => setShowRoleChange(false)}
                disabled={roleBusy}
                className="rounded bg-muted px-3 py-1 text-sm"
              >
                Cancel
              </button>
            </div>
          )}
          {roleError && <p className="text-destructive text-xs basis-full text-right">{roleError}</p>}
        </StatusRow>

        {/* Onboarding */}
        <StatusRow label="Onboarding">
          {onboarded ? (
            <Badge tone="ok">Completed {new Date(user.onboardingCompletedAt as number).toLocaleDateString()}</Badge>
          ) : (
            <Badge tone="muted">Not started</Badge>
          )}
          <button
            type="button"
            onClick={toggleOnboarding}
            disabled={onboardingBusy}
            className="rounded bg-muted hover:bg-border px-3 py-1.5 text-sm font-semibold text-foreground disabled:opacity-50"
          >
            {onboardingBusy ? "…" : onboarded ? "Reset onboarding" : "Mark onboarded"}
          </button>
          {onboardingError && <p className="text-destructive text-xs basis-full text-right">{onboardingError}</p>}
        </StatusRow>

        {/* Email */}
        <StatusRow label="Email">
          {emailVerified ? (
            <Badge tone="ok">Verified {new Date(user.emailVerifiedAt as number).toLocaleDateString()}</Badge>
          ) : (
            <Badge tone="muted">Unverified</Badge>
          )}
        </StatusRow>

        {/* Account */}
        <StatusRow label="Account">
          {user.locked ? <Badge tone="muted">Locked</Badge> : <Badge tone="ok">Active</Badge>}
          <button
            type="button"
            onClick={toggleLock}
            disabled={lockBusy}
            className={`rounded px-3 py-1.5 text-sm font-semibold border ${user.locked
              ? "bg-warning/20 hover:bg-warning/30 text-warning border-warning/40"
              : "bg-muted hover:bg-border text-foreground border-border"}`}
          >
            {lockBusy ? "…" : user.locked ? "Unlock account" : "Lock account"}
          </button>
          {lockError && <p className="text-destructive text-xs basis-full text-right">{lockError}</p>}
        </StatusRow>
      </div>
    </Section>
  );
}

const coachPreviewComponents: Components = {
  p: ({ children }) => (
    <p className="mt-3 first:mt-0 leading-relaxed text-muted-foreground">{children}</p>
  ),
  h1: ({ children }) => (
    <h3 className="mt-5 mb-1 first:mt-0 text-sm font-semibold text-foreground">{children}</h3>
  ),
  h2: ({ children }) => (
    <h3 className="mt-5 mb-1 first:mt-0 text-sm font-semibold text-foreground">{children}</h3>
  ),
  h3: ({ children }) => (
    <h3 className="mt-5 mb-1 first:mt-0 text-sm font-semibold text-foreground">{children}</h3>
  ),
  ul: ({ children }) => (
    <ul className="mt-3 first:mt-0 list-disc space-y-1 pl-5 text-muted-foreground">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mt-3 first:mt-0 list-decimal space-y-1 pl-5 text-muted-foreground">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  a: ({ children, href }) => (
    <a href={href} className="text-primary underline hover:text-primary-soft">{children}</a>
  ),
  code: ({ children }) => (
    <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">{children}</code>
  )
};

function CoachNoteCard({
  detail, userId, apiFetch, onMutate
}: {
  detail: DetailShape;
  userId: string;
  apiFetch: ReturnType<typeof useApi>["apiFetch"];
  onMutate: () => void;
}): JSX.Element {
  const existing = detail.coachNote;
  const fallback = detail.defaultCoachNote ?? { title: "", bodyMarkdown: "" };
  // Seed the editor with the saved note, or the default welcome note when the
  // user has none — so the coach edits from a template instead of a blank slate.
  const baseTitle = existing?.title ?? fallback.title;
  const baseBody = existing?.bodyMarkdown ?? fallback.bodyMarkdown;
  const [title, setTitle] = useState(baseTitle);
  const [body, setBody] = useState(baseBody);
  const [showPreview, setShowPreview] = useState(false);
  const [busy, setBusy] = useState<"save" | "clear" | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Dirty against the effective baseline: for a user with no saved note that's
  // the default, so Save stays disabled until the coach actually changes it.
  const dirty = title !== baseTitle || body !== baseBody;
  const canSave = title.trim().length > 0 && body.trim().length > 0 && dirty;

  async function save() {
    setBusy("save");
    setError(null);
    try {
      await saveCoachNote(apiFetch, userId, { title: title.trim(), bodyMarkdown: body.trim() });
      onMutate();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(null);
    }
  }

  async function clear() {
    setBusy("clear");
    setError(null);
    try {
      await clearCoachNote(apiFetch, userId);
      setTitle(fallback.title);
      setBody(fallback.bodyMarkdown);
      onMutate();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Section title="Coach note">
      <p className="text-xs text-muted-foreground mb-3">
        {existing
          ? `Shown on this user's home page. Last updated ${new Date(existing.updatedAt).toLocaleString()}${existing.updatedBy ? ` by ${existing.updatedBy}` : ""}.`
          : "This user currently sees the default welcome note, pre-filled below. Edit it to personalize their home page."}
      </p>

      <label className="block text-xs font-semibold text-foreground mb-1" htmlFor="coach-note-title">
        Title
      </label>
      <input
        id="coach-note-title"
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={200}
        placeholder="Welcome — let's get you started"
        className="w-full rounded bg-muted px-3 py-2 text-sm text-foreground mb-4"
        disabled={busy !== null}
      />

      <div className="flex items-center justify-between mb-1">
        <label className="block text-xs font-semibold text-foreground" htmlFor="coach-note-body">
          Note (markdown)
        </label>
        <button
          type="button"
          onClick={() => setShowPreview((v) => !v)}
          className="text-xs text-primary-soft hover:text-primary"
        >
          {showPreview ? "Edit" : "Preview"}
        </button>
      </div>

      {showPreview ? (
        <div className="min-h-[8rem] rounded border border-border-subtle bg-card px-4 py-3">
          {body.trim().length > 0 ? (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSanitize]}
              components={coachPreviewComponents}
            >
              {body}
            </ReactMarkdown>
          ) : (
            <p className="text-sm text-muted-foreground">Nothing to preview yet.</p>
          )}
        </div>
      ) : (
        <textarea
          id="coach-note-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={20000}
          rows={8}
          placeholder="Write a personal note for this learner. Markdown is supported."
          className="w-full rounded bg-muted px-3 py-2 text-sm text-foreground font-mono leading-relaxed"
          disabled={busy !== null}
        />
      )}

      <div className="flex flex-wrap items-center gap-2 mt-4">
        <button
          type="button"
          onClick={save}
          disabled={!canSave || busy !== null}
          className="rounded bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold disabled:opacity-50"
        >
          {busy === "save" ? "Saving…" : "Save note"}
        </button>
        {existing && (
          <button
            type="button"
            onClick={clear}
            disabled={busy !== null}
            className="rounded bg-muted hover:bg-border px-4 py-2 text-sm font-semibold text-foreground disabled:opacity-50"
          >
            {busy === "clear" ? "Clearing…" : "Clear (revert to default)"}
          </button>
        )}
      </div>
      {error && <p role="alert" className="text-destructive text-xs mt-2">{error}</p>}
    </Section>
  );
}

function DangerZoneCard({
  detail, userId, apiFetch, onDeleteSuccess
}: {
  detail: DetailShape;
  userId: string;
  apiFetch: ReturnType<typeof useApi>["apiFetch"];
  onDeleteSuccess: () => void;
}): JSX.Element {
  const { user } = detail;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function deleteUser() {
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await apiFetch(`/api/admin/users/${userId}`, { method: "DELETE" });
      setShowDeleteDialog(false);
      onDeleteSuccess();
    } catch (e) {
      setDeleteError(friendlyError(e));
      setDeleteBusy(false);
    }
  }

  return (
    <Section title="Danger zone" tone="danger" defaultOpen={false}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-md">
          Permanently delete this user and all of their lesson progress, saved work, activity events, onboarding responses, coach notes, and certificates. This cannot be undone.
        </p>
        <button
          type="button"
          onClick={() => { setShowDeleteDialog(true); setDeleteError(null); }}
          className="rounded bg-destructive/20 hover:bg-destructive/30 text-destructive border border-destructive/40 px-4 py-2 text-sm font-semibold"
        >
          Delete user
        </button>
      </div>
      {deleteError && <p className="text-destructive text-xs mt-2">{deleteError}</p>}
      {showDeleteDialog && (
        <ConfirmDangerDialog
          title="Delete user?"
          body={`This will permanently delete all of ${user.email}'s data — lesson progress, saved work, activity events, onboarding responses, coach notes, and certificates. This cannot be undone.`}
          confirmText={user.email}
          onConfirm={deleteUser}
          onCancel={() => setShowDeleteDialog(false)}
          busy={deleteBusy}
        />
      )}
    </Section>
  );
}

function ProgressCard({
  course,
  userId,
  email,
  apiFetch,
  onResetSuccess
}: {
  course: DetailShape["courses"][number];
  userId: string;
  email: string;
  apiFetch: ReturnType<typeof useApi>["apiFetch"];
  onResetSuccess: () => void;
}): JSX.Element {
  const [openDialog, setOpenDialog] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const completed = course.progress.completedLessons;

  const onConfirm = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await resetProgress(apiFetch, userId, course.courseSlug);
      setOpenDialog(false);
      onResetSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title={`Progress — ${course.courseTitle}`}>
      <div className="text-2xl font-semibold text-foreground">{completed.length} lessons completed</div>
      {completed.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">View completed lessons</summary>
          <ul className="mt-2 text-sm text-muted-foreground list-disc list-inside">
            {completed.map((id) => <li key={id} className="font-mono text-xs">{id}</li>)}
          </ul>
        </details>
      )}
      <PracticeActivityRow practiceActivity={course.practiceActivity} />
      <div className="border-t border-border pt-4 mt-2">
        <p className="text-xs text-muted-foreground mb-2">
          Resets lesson completion for this course. Saved project code and quiz answers are preserved.
        </p>
        <button
          type="button"
          onClick={() => setOpenDialog(true)}
          className="rounded bg-destructive/20 hover:bg-destructive/30 text-destructive border border-destructive/40 px-4 py-2 text-sm font-semibold"
        >
          Reset progress
        </button>
        {error && <p role="alert" className="text-destructive text-sm mt-2">{error}</p>}
        {openDialog && (
          <ConfirmDangerDialog
            title="Reset progress?"
            body={`This will clear ${email}'s completed-lesson record for ${course.courseTitle}. Saved project code and quiz answers are preserved.`}
            confirmText={email}
            onConfirm={onConfirm}
            onCancel={() => setOpenDialog(false)}
            busy={busy}
          />
        )}
      </div>
    </Section>
  );
}

// CF-10: per-course practice activity (try.run rollup).
// practiceActivity is null whenever the course-index entry has no matching
// rows for this user — either they never ran a try cell, or (for courses
// used before the Wave-4 python-emit fix) their python practice predates
// telemetry existing for that runtime, so it's undercounted rather than absent.
function PracticeActivityRow({
  practiceActivity
}: {
  practiceActivity: DetailShape["courses"][number]["practiceActivity"];
}): JSX.Element {
  if (!practiceActivity || practiceActivity.totalRuns === 0) {
    return (
      <div className="mt-3 text-sm">
        <span className="inline-block rounded bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground">
          No practice recorded
        </span>
      </div>
    );
  }
  const { totalRuns, distinctCells, lastRunAt } = practiceActivity;
  return (
    <div className="mt-3 text-sm text-muted-foreground">
      <span className="font-semibold text-foreground">{totalRuns}</span> practice run{totalRuns === 1 ? "" : "s"}
      {" "}across <span className="font-semibold text-foreground">{distinctCells}</span> distinct cell{distinctCells === 1 ? "" : "s"}
      {lastRunAt !== null && <> — last run {new Date(lastRunAt).toLocaleString()}</>}
      <p className="text-xs mt-1">
        May include undercounted Python practice from before run-tracking landed (2026-07-05).
      </p>
    </div>
  );
}

function ActivityCard({ detail }: { detail: DetailShape }): JSX.Element {
  const a = detail.activity;
  const events = detail.events;
  const [sortDir, setSortDir] = useState<"desc" | "asc">("desc");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const sorted = sortDir === "desc" ? [...events] : [...events].reverse();
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageEvents = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function toggleSort(): void {
    setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    setPage(1);
  }

  return (
    <Section title="Activity" defaultOpen={true}>
      <dl className="grid grid-cols-2 gap-y-2 text-sm mb-6">
        <dt className="text-muted-foreground">Lessons opened</dt>
        <dd>{a.lessonsOpenedCount}</dd>
        <dt className="text-muted-foreground">Lessons completed</dt>
        <dd>{a.lessonsCompletedCount}</dd>
        <dt className="text-muted-foreground">Chapters completed</dt>
        <dd>{a.chaptersCompletedCount}</dd>
        <dt className="text-muted-foreground">Days active (7d)</dt>
        <dd>{a.daysActive7d}</dd>
        <dt className="text-muted-foreground">First seen</dt>
        <dd>{a.firstSeen !== null ? new Date(a.firstSeen).toLocaleDateString() : "—"}</dd>
        <dt className="text-muted-foreground">Last seen</dt>
        <dd>{a.lastSeen !== null ? new Date(a.lastSeen).toLocaleString() : "—"}</dd>
      </dl>
      <div className="border-t border-border pt-4">
        {events.length === 0 ? (
          <p className="text-sm text-muted-foreground">No events yet.</p>
        ) : (
          <>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border-subtle">
                  <th className="pb-2 pr-4 font-medium">
                    <button
                      type="button"
                      onClick={toggleSort}
                      className="flex items-center gap-1 hover:text-foreground"
                    >
                      Timestamp
                      <span aria-hidden="true">{sortDir === "desc" ? "↓" : "↑"}</span>
                    </button>
                  </th>
                  <th className="pb-2 pr-4 font-medium">Event</th>
                  <th className="pb-2 pr-4 font-medium">Course</th>
                  <th className="pb-2 font-medium">Location</th>
                </tr>
              </thead>
              <tbody>
                {pageEvents.map((e) => (
                  <tr key={e.ts} className="border-b border-border-subtle last:border-0">
                    <td className="py-1.5 pr-4 text-muted-foreground whitespace-nowrap">
                      {new Date(e.ts).toLocaleString()}
                    </td>
                    <td className="py-1.5 pr-4 font-mono">{e.type}</td>
                    <td className="py-1.5 pr-4 text-muted-foreground">{e.courseSlug ?? ""}</td>
                    <td className="py-1.5 text-muted-foreground">
                      {e.chapter != null
                        ? e.lesson != null ? `${e.chapter}/${e.lesson}` : e.chapter
                        : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
              <span>
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} of {sorted.length}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="px-2 py-1 rounded border border-border disabled:opacity-40 hover:bg-muted/20 disabled:cursor-not-allowed"
                >
                  Prev
                </button>
                <button
                  type="button"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-2 py-1 rounded border border-border disabled:opacity-40 hover:bg-muted/20 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Section>
  );
}

function SavedWorkCard({ work }: { work: ReturnType<typeof useAdminUserWork> }): JSX.Element {
  return (
    <Section title="Saved work" defaultOpen={false}>
      {work.status === "loading" && <p className="text-muted-foreground text-sm">Loading work…</p>}
      {work.status === "error" && <p className="text-destructive text-sm">Failed to load: {work.message}</p>}
      {work.status === "ready" && work.items.length === 0 && (
        <p className="text-muted-foreground text-sm">No saved work yet.</p>
      )}
      {work.status === "ready" && work.items.length > 0 && (
        <ul className="space-y-3">
          {work.items.map((item) => <WorkItemRow key={`${item.chapter}/${item.lesson ?? "_capstone"}/${item.type}`} item={item} />)}
        </ul>
      )}
    </Section>
  );
}

function WorkItemRow({ item }: { item: AdminWorkItem }): JSX.Element {
  const label = item.type === "exercise"
    ? `Exercise · ${item.chapter}/${item.lesson}`
    : `Capstone · ${item.chapter}`;
  return (
    <li>
      <details>
        <summary className="cursor-pointer flex justify-between items-center text-sm">
          <span className="font-mono text-xs text-muted-foreground">{label}</span>
          <span className="text-xs text-muted-foreground">{new Date(item.savedAt).toLocaleString()} · {item.content.length} chars</span>
        </summary>
        <pre className="mt-2 max-h-64 overflow-auto rounded bg-muted text-foreground text-xs p-3 font-mono whitespace-pre-wrap">
{item.content}
        </pre>
      </details>
    </li>
  );
}
